import { PluginNotReadyError } from '@/lib/plugins/plugin-readiness'

export type PluginFlushOptions = {
  requireReady?: boolean
}

export type PluginFlushFunction = (options?: PluginFlushOptions) => Promise<boolean>

// Ties flush convergence to what the plugin components actually observed at
// their last React commit, instead of trusting that a resolved refetch has
// already reached their render closures.
export type FlushObservation = {
  // Serialized source versions (query dataUpdatedAt values) captured by the
  // plugins' last committed render.
  fingerprint: () => string
  // Bounded wait for React to commit renders triggered by cache changes. A
  // liveness heuristic only — it may return without a commit (e.g. an
  // invalidated query with no active observer never refetches). Correctness
  // comes from the consecutive-clean-pass fingerprint gate, not from this wait.
  awaitCommits: () => Promise<void>
  // Resolves on the next query/mutation cache event. Saves this flush follows
  // invalidate the plugins' source queries, so a plugin can report "not ready"
  // for a whole network roundtrip — far longer than `awaitCommits`, which
  // resolves on the next React commit. When supplied, readiness waits on cache
  // activity under `readinessTimeoutMs` instead of spending the pass budget.
  awaitReadinessChange?: () => Promise<void>
  readinessTimeoutMs?: number
}

const DEFAULT_READINESS_TIMEOUT_MS = 15_000

export function createSharedPluginFlush() {
  let active: { scopeKey: string; work: Promise<void> } | null = null
  return (scopeKey: string, start: () => Promise<void>) => {
    if (active?.scopeKey === scopeKey) return active.work
    const previous = active?.work
    const work = (previous ? previous.catch(() => undefined).then(start) : start())
    active = { scopeKey, work }
    void work.then(
      () => {
        if (active?.work === work) active = null
      },
      () => {
        if (active?.work === work) active = null
      }
    )
    return work
  }
}

export async function flushPluginFunctions(
  functions: PluginFlushFunction[],
  drain: () => Promise<boolean>,
  maxIterations: number,
  observation?: FlushObservation
) {
  // Fingerprint of the previous clean pass; null until one completes. The
  // flush only exits after two consecutive clean passes whose observed source
  // versions are identical — a single clean pass can be computed from stale
  // render closures that a pending React commit is about to replace.
  let cleanFingerprint: string | null = null
  // Verification (clean) passes get their own budget so they never spend the
  // mutation chain's iterations: a full details → tax → quote → milestone
  // chain plus the two-pass fingerprint gate must both fit.
  let mutationPasses = 0
  let verificationPasses = 0
  // A refetch cascade re-keys derived summary queries, leaving a window where
  // a plugin is legitimately not ready. With observation, that means "wait
  // for the next commit and retry", not "fail the flush" — but if it never
  // resolves, surface the readiness message instead of a convergence error.
  let lastNotReady: PluginNotReadyError | null = null
  // When the flush can wait on cache activity, readiness gets its own deadline
  // rather than a pass budget: the refetches it waits for are network-bound.
  let readinessWaitStartedAt: number | null = null
  while (mutationPasses < maxIterations && verificationPasses < maxIterations) {
    const settled = await Promise.allSettled(functions.map((fn) => fn({ requireReady: true })))
    const rejections = settled.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    if (rejections.length) {
      const fatal = rejections.find((result) => !(result.reason instanceof PluginNotReadyError))
      if (fatal || !observation) throw rejections[0].reason
      lastNotReady = rejections[0].reason as PluginNotReadyError
      cleanFingerprint = null
      if (observation.awaitReadinessChange) {
        readinessWaitStartedAt ??= Date.now()
        if (Date.now() - readinessWaitStartedAt >= (observation.readinessTimeoutMs ?? DEFAULT_READINESS_TIMEOUT_MS)) {
          throw lastNotReady
        }
        // Waits on a cache event, so this is not a spin — and it deliberately
        // spends no pass budget, leaving convergence verification intact.
        await observation.awaitReadinessChange()
        continue
      }
      verificationPasses++
      await observation.awaitCommits()
      continue
    }
    lastNotReady = null
    readinessWaitStartedAt = null
    const anyMutated = settled.some((result) => result.status === 'fulfilled' && result.value === true)
    const anyInvalidated = await drain()
    if (anyMutated || anyInvalidated) {
      mutationPasses++
      cleanFingerprint = null
      await observation?.awaitCommits()
      continue
    }
    if (!observation) return
    verificationPasses++
    const fingerprint = observation.fingerprint()
    if (cleanFingerprint !== null && fingerprint === cleanFingerprint) return
    cleanFingerprint = fingerprint
    await observation.awaitCommits()
  }
  if (lastNotReady) throw lastNotReady
  throw new Error(`CPQ plugin flush did not converge after ${maxIterations} iterations.`)
}
